import { components, useApi } from "@appretio/api";
import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useImperativeFetch } from "@appretio/shared";
import { useCallback } from "react";

/**
 * Response from the forecast summary endpoint.
 *
 * Re-exported from the generated OpenAPI schema rather than hand-rolled
 * so any backend change to ``ForecastSummaryOutput`` surfaces as a TS
 * error in the callers instead of a silent runtime shape drift.
 */
export type ForecastSummaryData =
  components["schemas"]["ForecastSummaryOutput"];

/**
 * Parameters for loading forecast summary data.
 *
 * ``cubeId`` carries either a DimCube UUID (user/budget cubes) or a DS
 * version string (analytics cubes); the server resolves the producer.
 */
interface FetchForecastSummaryParams {
  context: ForecastContextSelectedOptions;
  cubeId: string;
  year: string;
  tableDim: string;
  rowDim: string;
  indexCubeId?: string;
  indexYear?: string;
}

export function useFetchForecastSummaryData() {
  const apiClient = useApi();

  const fetchFn = useCallback(
    async (
      params: FetchForecastSummaryParams
    ): Promise<ForecastSummaryData> => {
      const response = await apiClient.POST("/v2/forecast/summary/get", {
        body: {
          context: params.context ?? {},
          cube_id: params.cubeId,
          year: params.year,
          table_dim: params.tableDim,
          row_dim: params.rowDim,
          index_cube_id: params.indexCubeId || undefined,
          index_year: params.indexYear || undefined
        }
      });
      const result = response.data?.result;
      if (!result) {
        throw new Error("Missing result in forecast summary response");
      }
      return result;
    },
    [apiClient]
  );

  return useImperativeFetch(fetchFn, {
    errorMessage: "Failed to load forecast summary"
  });
}
